"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Layers2, MousePointer, Trash2, RotateCw, Maximize2, ChevronLeft, Plus } from "lucide-react"
import { cn } from "../../../lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClipartEntry {
  index: number      // index into window.arrayGroupCliparts
  thumbUrl: string   // webp thumb for display
}

// ─── Categories ───────────────────────────────────────────────────────────────

const CATEGORIES: Record<string, number> = {
  simulador: 743, Backgrounds: 138, Animais: 30, Baseball: 11, Basketball: 23,
  Beer: 33, Cannabis: 19, carros: 89, ciclismo: 31, Downhill: 38, et: 10,
  Formandos: 199, hacker: 64, mascots: 233, MOTOCROSS: 57, Motorcycle: 96,
  Patterns: 302, Pescaria: 44, Rafting: 23, scudos: 132, Skate: 18, Surf: 61, Custom: 0,
}

const SUPABASE = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/cliparts"

function getThumbUrl(category: string, index: number) {
  return category === "simulador"
    ? `./patterns/${index}.webp`
    : `${SUPABASE}/thumbs/${category}/${index}.webp`
}

function getSvgUrl(category: string, index: number) {
  return category === "simulador"
    ? `./patterns/${index}.svg`
    : `${SUPABASE}/svgs/${category}/${index}.svg`
}

function readCliparts(): ClipartEntry[] {
  const arr: any[] = (window as any).arrayGroupCliparts ?? []
  return arr.map((obj, i) => ({
    index: i,
    thumbUrl: obj?._thumbUrl ?? "",
  })).filter((_, i) => arr[i] != null)
}

// ─── Lazy clipart thumbnail ───────────────────────────────────────────────────

function ClipartItem({ category, index, onSelect }: {
  category: string; index: number; onSelect: (svgUrl: string, thumbUrl: string) => void
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const [bg, setBg] = useState("")
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return
      observer.unobserve(el)
      const url = getThumbUrl(category, index)
      const img = new Image()
      img.src = url
      img.onload = () => { setBg(url); setLoaded(true) }
      img.onerror = () => setLoaded(true)
    }, { rootMargin: "300px", threshold: 0.01 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [category, index])

  return (
    <button
      ref={ref}
      onClick={() => onSelect(getSvgUrl(category, index), getThumbUrl(category, index))}
      className={cn(
        "w-16 h-16 rounded-xl border-2 border-transparent hover:border-primary/60 bg-secondary/30 bg-center bg-contain bg-no-repeat transition-all hover:scale-105 active:scale-95 touch-manipulation flex-shrink-0",
        !loaded && "animate-pulse"
      )}
      style={bg ? { backgroundImage: `url("${bg}")` } : undefined}
    />
  )
}

// ─── Waiting for click ────────────────────────────────────────────────────────

function WaitingForClick({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-primary/60 bg-primary/5 p-4 flex flex-col items-center gap-3 text-center animate-in fade-in duration-200">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
        <MousePointer className="w-5 h-5 text-primary" />
      </div>
      <p className="text-sm font-semibold text-foreground">Clique no uniforme</p>
      <p className="text-xs text-muted-foreground -mt-2">Toque onde quer posicionar a estampa</p>
      <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
        Cancelar
      </button>
    </div>
  )
}

// ─── Added clipart card ───────────────────────────────────────────────────────

function ClipartCard({ entry, onEdit }: { entry: ClipartEntry; onEdit: (e: ClipartEntry) => void }) {
  return (
    <button
      onClick={() => onEdit(entry)}
      className="group relative w-full aspect-square rounded-2xl border-2 border-border/40 hover:border-primary/60 bg-secondary/20 overflow-hidden transition-all hover:scale-[1.03] active:scale-95 touch-manipulation bg-center bg-contain bg-no-repeat"
      style={entry.thumbUrl ? { backgroundImage: `url("${entry.thumbUrl}")` } : undefined}
    >
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <span className="text-white text-xs font-semibold">Editar</span>
      </div>
    </button>
  )
}

// ─── Edit panel ───────────────────────────────────────────────────────────────

function ClipartEditPanel({ entry, onBack, onDeleted }: {
  entry: ClipartEntry
  onBack: () => void
  onDeleted: () => void
}) {
  const w = window as any
  const obj = w.arrayGroupCliparts?.[entry.index]

  const initialScale = (() => {
    if (!obj) return 50
    const MIN_H = 50, MAX_H = 2000
    const t = ((obj.height * obj.scaleY) - MIN_H) / (MAX_H - MIN_H)
    return Math.round(Math.max(0, Math.min(1, t)) * 100)
  })()

  const [scale, setScale] = useState(initialScale)
  const [angle, setAngle] = useState(Math.round(obj?.angle ?? 0))

  useEffect(() => {
    if (!obj) return
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")
  }, [obj])

  const exitEdit = () => {
    w.editingImage = false
    w.targetImage = null
    w["fabricRes"]?.("high")
    w["setupCamera"]?.()
    if (w.cube) w.cube.visible = false
  }

  const handleScale = (v: number) => {
    setScale(v)
    if (!obj) return
    const MIN_H = 50, MAX_H = 2000
    const t = v / 100
    const targetH = MIN_H + (MAX_H - MIN_H) * t
    const scaleVal = (targetH / obj.height) * (obj.iniScale ?? 1) * 8
    const center = obj.getCenterPoint()
    obj.set({ originX: "center", originY: "center", left: center.x, top: center.y, scaleX: scaleVal, scaleY: scaleVal })
    obj.setCoords()
    w.fabricCanvas?.requestRenderAll()
    setTimeout(() => w["updateCanvas"]?.(), 50)
  }

  const handleAngle = (v: number) => {
    setAngle(v)
    if (!obj) return
    const center = obj.getCenterPoint()
    obj.set({ originX: "center", originY: "center", left: center.x, top: center.y, angle: v })
    obj.setCoords()
    w.fabricCanvas?.requestRenderAll()
    setTimeout(() => w["updateCanvas"]?.(), 50)
  }

  const handleDelete = () => {
    if (!obj) return
    if (obj.group) obj.group.removeWithUpdate(obj)
    w.fabricCanvas?.remove(obj)
    w.fabricCanvas?.renderAll()
    w["updateCanvas"]?.()
    w.arrayGroupCliparts[entry.index] = null
    exitEdit()
    window.dispatchEvent(new Event("clipartsChanged"))
    onDeleted()
  }

  return (
    <div className="space-y-4 animate-in slide-in-from-right-4 duration-200">

      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={() => { exitEdit(); onBack() }}
          className="w-8 h-8 rounded-xl bg-secondary/60 hover:bg-secondary active:scale-95 flex items-center justify-center transition-all touch-manipulation flex-shrink-0">
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-semibold text-foreground">Editar Estampa</span>
        {entry.thumbUrl && (
          <div className="ml-auto w-10 h-10 rounded-xl border border-border/40 bg-secondary/30 bg-center bg-contain bg-no-repeat flex-shrink-0"
            style={{ backgroundImage: `url("${entry.thumbUrl}")` }} />
        )}
      </div>

      {/* Scale */}
      <div className="space-y-2 rounded-2xl bg-secondary/20 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Tamanho</span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{scale}%</span>
        </div>
        <input type="range" min={0} max={100} value={scale}
          onChange={e => handleScale(Number(e.target.value))}
          className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
      </div>

      {/* Rotation */}
      <div className="space-y-2 rounded-2xl bg-secondary/20 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RotateCw className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Rotação</span>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{angle}°</span>
        </div>
        <input type="range" min={0} max={360} value={angle}
          onChange={e => handleAngle(Number(e.target.value))}
          className="w-full h-2 rounded-full accent-primary cursor-pointer touch-manipulation" />
      </div>

      {/* Delete */}
      <button onClick={handleDelete}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 transition-all touch-manipulation">
        <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0" />
        <span className="text-sm font-medium text-red-400">Remover estampa</span>
      </button>
    </div>
  )
}

// ─── Main LayersPicker ────────────────────────────────────────────────────────

export function LayersPicker() {
  const [added, setAdded] = useState<ClipartEntry[]>([])
  const [editing, setEditing] = useState<ClipartEntry | null>(null)
  const [showBrowser, setShowBrowser] = useState(false)
  const [activeCategory, setActiveCategory] = useState("simulador")
  const [waitingForClick, setWaitingForClick] = useState(false)
  const prevCountRef = useRef(0)
  const gridRef = useRef<HTMLDivElement>(null)

  const refreshAdded = useCallback(() => setAdded(readCliparts()), [])

  useEffect(() => {
    // Ensure the array exists
    if (!(window as any).arrayGroupCliparts) (window as any).arrayGroupCliparts = []
    refreshAdded()

    const onLayout = () => {
      setAdded([]); setEditing(null); setWaitingForClick(false)
      setShowBrowser(false); prevCountRef.current = 0
    }
    window.addEventListener("layoutChanged", onLayout)
    window.addEventListener("canvasCleared", onLayout)
    window.addEventListener("clipartsChanged", refreshAdded)
    return () => {
      window.removeEventListener("layoutChanged", onLayout)
      window.removeEventListener("canvasCleared", onLayout)
      window.removeEventListener("clipartsChanged", refreshAdded)
    }
  }, [refreshAdded])

  // Poll for placement after user clicks 3D
  useEffect(() => {
    if (!waitingForClick) return
    const interval = setInterval(() => {
      const arr: any[] = (window as any).arrayGroupCliparts ?? []
      const curr = arr.filter(Boolean).length
      if (curr > prevCountRef.current) {
        prevCountRef.current = curr
        setWaitingForClick(false)
        setShowBrowser(false)
        window.dispatchEvent(new Event("logoPlacementEnd"))
        refreshAdded()
        clearInterval(interval)
      }
    }, 150)
    return () => clearInterval(interval)
  }, [waitingForClick, refreshAdded])

  const handleSelect = (svgUrl: string, thumbUrl: string) => {
    const w = window as any
    if (!w.arrayGroupCliparts) w.arrayGroupCliparts = []

    w.addClipart = true
    w.editingImage = true

    const slotIndex = w.arrayGroupCliparts.length

    // Override addClipartIframe to capture the placed object and store with thumb
    const orig = w["addClipartIframe"]
    w["addClipartIframe"] = function () {
      const { fabric } = w
      w["stateLoadinScreen"]?.(true)
      fabric.loadSVGFromURL(svgUrl, (objects: any, options: any) => {
        const svg = fabric.util.groupSVGElements(objects, options)
        svg.scaleToHeight(900)
        svg.set({
          left: w.pointer.x,
          top: w.pointer.y,
          originX: "center",
          originY: "center",
          id: "pattern" + Math.random(),
          iniScale: svg.scaleX,
          _thumbUrl: thumbUrl,   // store thumb for the UI
        })
        w.fabricCanvas.add(svg).renderAll()
        w.updateCanvas?.()
        w["stateLoadinScreen"]?.(false)
        w.targetImage = svg
        w.arrayGroupCliparts.push(svg)
        w["addClipartIframe"] = orig
      })
    }

    prevCountRef.current = (w.arrayGroupCliparts ?? []).filter(Boolean).length
    setWaitingForClick(true)
    window.dispatchEvent(new Event("logoPlacementStart"))
    w['toast']?.("Clique na roupa onde vc quer posicionar o clipart!", "info")
  }

  const handleCancelWaiting = () => {
    const w = window as any
    w.addClipart = false
    w.editingImage = false
    setWaitingForClick(false)
    window.dispatchEvent(new Event("logoPlacementEnd"))
  }

  const handleEdit = (entry: ClipartEntry) => {
    const w = window as any
    const obj = w.arrayGroupCliparts?.[entry.index]
    if (!obj) return
    w.targetImage = obj
    w.editingImage = true
    w["fabricRes"]?.("low")
    setEditing(entry)
  }

  const categoryNames = Object.keys(CATEGORIES)
  const count = CATEGORIES[activeCategory] ?? 0

  // ── Edit panel ──
  if (editing) {
    return (
      <div className="space-y-4">
        <LayersHeader />
        <ClipartEditPanel
          entry={editing}
          onBack={() => setEditing(null)}
          onDeleted={() => { setEditing(null); refreshAdded() }}
        />
      </div>
    )
  }

  // ── Browser (category + grid) ──
  if (showBrowser) {
    return (
      <div className="space-y-4">
        <LayersHeader />
        {waitingForClick
          ? <WaitingForClick onCancel={handleCancelWaiting} />
          : <>
              {/* Back to list */}
              <button onClick={() => setShowBrowser(false)}
                className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors touch-manipulation">
                <ChevronLeft className="w-3.5 h-3.5" /> Voltar para estampas adicionadas
              </button>

              {/* Category tabs */}
              <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                {categoryNames.map(name => (
                  <button key={name} onClick={() => {
                    setActiveCategory(name)
                    if (gridRef.current) gridRef.current.scrollTop = 0
                  }}
                    className={cn(
                      "px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all touch-manipulation flex-shrink-0",
                      activeCategory === name
                        ? "bg-primary text-primary-foreground shadow"
                        : "bg-secondary/40 text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                    )}>
                    {name}
                  </button>
                ))}
              </div>

              {/* Grid */}
              {count === 0
                ? <p className="text-center py-8 text-sm text-muted-foreground">Nenhuma estampa nesta categoria</p>
                : <div ref={gridRef} className="flex flex-wrap gap-2 max-h-[50vh] overflow-y-auto rounded-2xl bg-secondary/10 p-2.5">
                    {Array.from({ length: count }, (_, i) => {
                      const idx = activeCategory === "simulador" ? i + 1 : i
                      return (
                        <ClipartItem key={`${activeCategory}-${i}`}
                          category={activeCategory} index={idx}
                          onSelect={handleSelect} />
                      )
                    })}
                  </div>
              }
            </>
        }
      </div>
    )
  }

  // ── Default: list of added cliparts ──
  return (
    <div className="space-y-4">
      <LayersHeader />

      {added.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {added.map(entry => (
            <ClipartCard key={entry.index} entry={entry} onEdit={handleEdit} />
          ))}
        </div>
      )}

      {added.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
          <div className="w-14 h-14 rounded-2xl bg-secondary/40 flex items-center justify-center">
            <Layers2 className="w-7 h-7 text-muted-foreground/50" />
          </div>
          <p className="text-sm text-muted-foreground max-w-[180px]">Nenhuma estampa adicionada ainda</p>
        </div>
      )}

      <button onClick={() => setShowBrowser(true)}
        className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/50 hover:border-primary/60 bg-secondary/10 hover:bg-primary/5 transition-all touch-manipulation group">
        <Plus className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
        <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
          Adicionar estampa
        </span>
      </button>
    </div>
  )
}

function LayersHeader() {
  return (
    <div className="flex items-center gap-3">
      <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
        <Layers2 className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h3 className="font-semibold text-foreground">Estampas</h3>
        <p className="text-xs text-muted-foreground">Adicione e edite estampas no uniforme</p>
      </div>
    </div>
  )
}